/**
 * Typed wrapper for the citizen virtual debit card endpoints on
 * service-financial-services (port 3110).
 *
 * Endpoints:
 *   - POST /v1/cards/request          — request a new virtual card
 *   - GET  /v1/cards/me               — list citizen's cards
 *   - POST /v1/cards/:id/freeze       — ACTIVE → FROZEN
 *   - POST /v1/cards/:id/unfreeze     — FROZEN → ACTIVE
 *   - POST /v1/cards/:id/cancel       — terminal CANCELLED
 *
 * RBAC:
 *   - request → Card/issue-own
 *   - me      → Card/read-own
 *   - freeze/unfreeze/cancel → Card/freeze-own
 */
import { requestJson, type RequestJsonOptions } from '../api-client';
import { getCustomerToken } from '../auth/tokenStore';
import { env } from '../env';

export interface VirtualCard {
  readonly id: string;
  readonly brand: string;
  readonly last4: string;
  readonly expMonth: number;
  readonly expYear: number;
  readonly status: 'ACTIVE' | 'FROZEN' | 'CANCELLED';
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** Returned by POST /request — includes one-time PAN. */
export interface IssuedVirtualCard extends VirtualCard {
  readonly pan: string;
}

export interface ListMyVirtualCardsResponse {
  readonly items: readonly VirtualCard[];
}

type CallOpts = Pick<RequestJsonOptions, 'fetchImpl' | 'signal'> & {
  readonly bearerToken?: string;
};

function bearerOf(opts: CallOpts): string | undefined {
  return opts.bearerToken ?? getCustomerToken() ?? undefined;
}

export async function requestVirtualCard(
  options: CallOpts = {},
): Promise<IssuedVirtualCard> {
  const { bearerToken: _ignore, ...rest } = options;
  return requestJson<IssuedVirtualCard>({
    method: 'POST',
    baseUrl: env.serviceFinancialServicesBaseUrl,
    path: '/v1/cards/request',
    body: {},
    bearerToken: bearerOf(options),
    ...rest,
  });
}

export async function listMyVirtualCards(
  options: CallOpts = {},
): Promise<ListMyVirtualCardsResponse> {
  const { bearerToken: _ignore, ...rest } = options;
  return requestJson<ListMyVirtualCardsResponse>({
    method: 'GET',
    baseUrl: env.serviceFinancialServicesBaseUrl,
    path: '/v1/cards/me',
    bearerToken: bearerOf(options),
    ...rest,
  });
}

async function transition(action: 'freeze' | 'unfreeze' | 'cancel', id: string, options: CallOpts): Promise<VirtualCard> {
  const { bearerToken: _ignore, ...rest } = options;
  return requestJson<VirtualCard>({
    method: 'POST',
    baseUrl: env.serviceFinancialServicesBaseUrl,
    path: `/v1/cards/${encodeURIComponent(id)}/${action}`,
    body: {},
    bearerToken: bearerOf(options),
    ...rest,
  });
}

export const freezeVirtualCard = (id: string, options: CallOpts = {}): Promise<VirtualCard> =>
  transition('freeze', id, options);
export const unfreezeVirtualCard = (id: string, options: CallOpts = {}): Promise<VirtualCard> =>
  transition('unfreeze', id, options);
export const cancelVirtualCard = (id: string, options: CallOpts = {}): Promise<VirtualCard> =>
  transition('cancel', id, options);
